import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/context/AppContext';
import api from '@/lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Bell, Receipt, AlarmClock, CheckCheck } from 'lucide-react';

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function NotificationBell() {
  const { user } = useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const isManagerUp = user && (user.role === 'OWNER' || user.role === 'MANAGER' || user.owner_access);

  const { data } = useQuery({
    queryKey: ['notif-bell'],
    queryFn: async () => (await api.get('/notifications?limit=25')).data.data,
    enabled: !!isManagerUp,
    refetchInterval: 30000,
    staleTime: 15000,
  });

  if (!isManagerUp) return null;
  const items = data?.items || [];
  const unread = data?.unread || 0;

  const onOpenChange = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      try { await api.post('/notifications/seen'); qc.invalidateQueries({ queryKey: ['notif-bell'] }); } catch (e) { /* ignore */ }
    }
  };

  const goTo = (n) => {
    setOpen(false);
    navigate(n.kind === 'sale' ? '/sales' : '/due-reminders');
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" title="Notifications"
          className="relative flex h-9 w-9 items-center justify-center rounded-full hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notif-bell-badge" className="absolute -right-0.5 -top-0.5 flex min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold leading-4 text-destructive-foreground">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
          <p className="text-sm font-semibold">Notifications</p>
          {unread > 0 && <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><CheckCheck className="h-3.5 w-3.5" />{unread} new</span>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Bell className="mx-auto h-6 w-6 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">No notifications yet</p>
            </div>
          ) : items.map((n) => {
            const Icon = n.kind === 'sale' ? Receipt : AlarmClock;
            return (
              <button key={n.id} onClick={() => goTo(n)} data-testid={`notif-item-${n.id}`}
                className="flex w-full items-start gap-2.5 border-b border-border px-3 py-2.5 text-left last:border-0 hover:bg-accent/50">
                <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${n.kind === 'sale' ? 'bg-emerald-500/15 text-emerald-600' : 'bg-amber-500/15 text-amber-600'}`}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{n.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{n.body}</p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</p>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
